import { formatDate } from "@/shared/formats";
import { CajaApertura } from "@/shared/types/caja-apertura";
import { Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import PrintIcon from '@mui/icons-material/Print';
import SharedCajaAperturaTransferenciaMetodoSummaryTable from "./shared-caja-apertura-transferencia-metodo-summary-table";
import SharedCajaAperturaTotalTable from "./shared-caja-apertura-total-table";
import useGetCajaAperturaMetodosMonedas from "../hooks/use-get-caja-apertura-metodos-monedas";
import { usePrinter } from "@/components/print";
import { Form, useForm } from "@/components/form";
import PrintCajaApertura from "./print-caja-apertura";

export interface SharedViewCajaAperturaProps {
    cajaApertura: CajaApertura,
    onViewMovimientosClick?: (e: React.MouseEvent<HTMLButtonElement>, cajaApertura: CajaApertura) => void
}

export default function SharedViewCajaApertura({
    cajaApertura,
    onViewMovimientosClick
}: SharedViewCajaAperturaProps) {
    const [transferenciaMetodos, metodoMonedasMap, monedas] = useGetCajaAperturaMetodosMonedas(cajaApertura);

    const printer = usePrinter();
    const printForm = useForm({
        onSubmit: async () => {
            await printer.print({
                documentTitle: `Apertura de Caja - ${formatDate(cajaApertura.fechaApertura)}`,
                printComponent: <PrintCajaApertura cajaApertura={cajaApertura} />
            })
        },
        closeModalOnSubmit: false,
        submitOnce: false,
    })

    return <>
        <Stack gap={1}>
            <Typography variant="h6">Información General</Typography>
            <AttributeValuePair label="Fecha de Apertura: " value={formatDate(cajaApertura.fechaApertura, true)} />
            <AttributeValuePair label="Fecha de Cierre: " value={formatDate(cajaApertura.fechaCierre, true)} />
            <AttributeValuePair label="Caja: " value={cajaApertura.cajaNombre} />
            <AttributeValuePair label="Estado: " value={cajaApertura.abierto ? "Abierto" : "Cerrado"} />
        </Stack>
        <Stack gap={1}>
            <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
                <Typography variant="h6">Montos</Typography>
                <Form form={printForm} direction={"row"} gap={2}>
                    <Form.Button size="small" startIcon={<FormatListBulletedIcon />} onClick={e => onViewMovimientosClick && onViewMovimientosClick(e, cajaApertura)}>
                        Movimientos
                    </Form.Button>
                    <Form.SubmitButton variant="contained" size="small" startIcon={<PrintIcon />}>
                        Imprimir
                    </Form.SubmitButton>
                </Form>
            </Stack>
            {transferenciaMetodos.map(tm => <SharedCajaAperturaTransferenciaMetodoSummaryTable
                key={tm.id}
                cajaApertura={cajaApertura}
                transferenciaMetodo={tm}
                monedas={metodoMonedasMap[tm.id]}
            />)}
        </Stack>
        <Stack direction={"row"}>
            <SharedCajaAperturaTotalTable cajaApertura={cajaApertura} monedas={monedas} />
        </Stack>
        <printer.Printer />
    </>
}

function AttributeValuePair({
    label,
    value
}: {
    label: ReactNode,
    value: ReactNode
}) {
    return <Stack direction={"row"} gap={2} marginLeft={5}>
        <Typography variant="subtitle1">{label}</Typography>
        <Typography variant="body1">{!value && value !== 0 ? "---" : value}</Typography>
    </Stack>
}